import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Toast from '../../components/common/Toast';
import { apiService } from '../../services/api';
import {
  JudgeAllowanceConflict, MAX_ALLOWANCE, conflictJudges, errorMessage, setJudgeAllowance,
} from '../../services/manageApi';

interface AccessEntry {
  username: string;
  access_type: string;
  first_name?: string | null;
  last_name?: string | null;
  current_votes?: number | null;
  max_votes?: number | null;
  completed_evaluations?: number | null;
}

interface JudgeRow {
  username: string;
  name: string;
  used: number;
  maxVotes: number;
}

interface Props {
  competitionId: number;
  canManage: boolean;
  /** Bump to reload (e.g. after a judging reset or a submission delete). */
  refreshKey: number;
  onDirtyChange: (section: string, dirty: boolean) => void;
}

const SECTION = 'judges';

function toRow(entry: AccessEntry): JudgeRow {
  const name = [entry.first_name, entry.last_name].filter(Boolean).join(' ').trim() || entry.username;
  return {
    username: entry.username,
    name,
    // Rubric competitions count evaluations; use whichever is larger, like the server.
    used: Math.max(entry.current_votes ?? 0, entry.completed_evaluations ?? 0),
    maxVotes: entry.max_votes ?? 0,
  };
}

function parseAllowance(value: string): number | null {
  const n = Number(value);
  return value.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= MAX_ALLOWANCE ? n : null;
}

const JudgesAllowanceSection: React.FC<Props> = ({ competitionId, canManage, refreshKey, onDirtyChange }) => {
  const [judges, setJudges] = useState<JudgeRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [bulk, setBulk] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [blocked, setBlocked] = useState<JudgeAllowanceConflict[]>([]);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await apiService.get(`/competitions/${competitionId}/access`);
      const entries: AccessEntry[] = res.data?.entries || [];
      setJudges(entries.filter((e) => e.access_type === 'judge').map(toRow));
      setDrafts({});
    } catch (err) {
      setLoadError(errorMessage(err, "Couldn't load judges"));
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const dirty = useMemo(
    () => judges.some((j) => drafts[j.username] !== undefined && drafts[j.username] !== String(j.maxVotes)),
    [judges, drafts],
  );

  useEffect(() => {
    onDirtyChange(SECTION, dirty);
  }, [dirty, onDirtyChange]);

  const showError = (message: string, conflicts: JudgeAllowanceConflict[] = []) => {
    setError(message);
    setBlocked(conflicts);
  };

  const saveOne = async (judge: JudgeRow) => {
    const value = parseAllowance(drafts[judge.username] ?? '');
    if (value === null) {
      showError(`Enter a whole number from 0 to ${MAX_ALLOWANCE}.`);
      return;
    }
    if (value < judge.used) {
      showError(`${judge.username} has already used ${judge.used} vote(s); the allowance can't be lower.`);
      return;
    }
    setSaving(judge.username);
    showError('');
    try {
      await setJudgeAllowance(competitionId, value, [judge.username]);
      setJudges((prev) => prev.map((j) => (j.username === judge.username ? { ...j, maxVotes: value } : j)));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[judge.username];
        return next;
      });
      setToast(`Allowance for ${judge.username} set to ${value}`);
    } catch (err) {
      showError(errorMessage(err, 'Failed to save allowance'), conflictJudges(err));
    } finally {
      setSaving(null);
    }
  };

  const applyAll = async () => {
    const value = parseAllowance(bulk);
    if (value === null) {
      showError(`Enter a whole number from 0 to ${MAX_ALLOWANCE}.`);
      return;
    }
    if (!window.confirm(`Set the allowance of all ${judges.length} judge(s) to ${value}?`)) return;
    setSaving('__all__');
    showError('');
    try {
      await setJudgeAllowance(competitionId, value);
      setBulk('');
      // Reload: the server only updates judges that have a Judges row, so don't assume every row changed.
      await load();
      setToast(`All judges set to ${value}`);
    } catch (err) {
      showError(errorMessage(err, 'Failed to update allowances'), conflictJudges(err));
    } finally {
      setSaving(null);
    }
  };

  let body: React.ReactNode;
  if (loading) {
    body = <LoadingSpinner size="sm" text="Loading judges..." />;
  } else if (loadError) {
    body = (
      <div className="manage-error" role="alert">
        {loadError} <Button variant="ghost" size="sm" onClick={load}>Retry</Button>
      </div>
    );
  } else if (judges.length === 0) {
    body = <p className="manage-field__help">No judges yet — add them from People → Add Access.</p>;
  } else {
    body = (
      <>
        {canManage && (
          <div className="judges-bulk">
            <label htmlFor="judges-bulk-input">Set all judges to</label>
            <input
              id="judges-bulk-input"
              type="number"
              min={0}
              max={MAX_ALLOWANCE}
              className="input input--default input--md"
              value={bulk}
              onChange={(e) => {
                setBulk(e.target.value);
                showError('');
              }}
              disabled={saving !== null}
            />
            <Button variant="secondary" size="sm" disabled={!bulk.trim() || saving !== null} onClick={applyAll}>
              Apply to all
            </Button>
          </div>
        )}
        <table className="judges-table">
          <thead>
            <tr>
              <th>Judge</th>
              <th>Used</th>
              <th>Allowance</th>
              {canManage && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {judges.map((judge) => {
              const draft = drafts[judge.username] ?? String(judge.maxVotes);
              const changed = draft !== String(judge.maxVotes);
              return (
                <tr key={judge.username}>
                  <td>
                    {judge.name}
                    {judge.name !== judge.username && <span className="judges-table__username"> ({judge.username})</span>}
                  </td>
                  <td>{judge.used}</td>
                  <td>
                    {canManage ? (
                      <input
                        type="number"
                        min={judge.used}
                        max={MAX_ALLOWANCE}
                        aria-label={`Allowance for ${judge.username}`}
                        className="input input--default input--md judges-table__input"
                        value={draft}
                        onChange={(e) => {
                          setDrafts((prev) => ({ ...prev, [judge.username]: e.target.value }));
                          showError('');
                        }}
                        disabled={saving !== null}
                      />
                    ) : (
                      judge.maxVotes
                    )}
                  </td>
                  {canManage && (
                    <td>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={`Save allowance for ${judge.username}`}
                        disabled={!changed || saving !== null}
                        onClick={() => saveOne(judge)}
                      >
                        Save
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </>
    );
  }

  return (
    <section className="manage-section" data-tour="manage-judges">
      <h3>Judges</h3>
      <p className="manage-field__help">
        How many votes or evaluations each judge may cast. It can&apos;t go below what a judge has already used.
        {!canManage && ' Only host admins and platform admins can change it.'}
      </p>
      {body}
      {error && (
        <div className="manage-error" role="alert">
          {error}
          {blocked.length > 0 && (
            <ul>
              {blocked.map((b) => <li key={b.username}>{b.username}: {b.used} already used</li>)}
            </ul>
          )}
        </div>
      )}
      {toast && <Toast message={toast} duration={2500} onClose={() => setToast('')} />}
    </section>
  );
};

export default JudgesAllowanceSection;
